import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as ClearFilter } from '../../../../assets/icons/filter-close.svg';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../shared-components/button/Button';
import CustomDatePicker from '../../../../shared-components/custom-date-range/CustomDatePicker';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import endConsumerV2Api from '../../../../utils/api/end-consumer-v2';

/**
 * API activity log item from v2 activity-log API
 */
interface ActivityApiModel {
  _id: string;
  userCustomId?: string;
  chargingPointSn?: string;
  title: string;
  message: string;
  status?: string;
  createdAt: string;
  updatedAt?: string;
  __v?: number;
}

/**
 * UI timeline activity
 */
interface TimelineActivity {
  id: string;
  title: string;
  description: string;
  timestamp: string;
  chargingPointSn?: string;
  status?: string;
}

/**
 * Activity timeline properties
 */
interface ActivityTimelineProperties {
  userCustomId?: string;
}

/**
 * Extracts raw list data from API response
 * @param {any} responseData - The response data object or array
 * @returns {any[]} The extracted array of activities
 */
const extractActivityList = (responseData: any): any[] => {
  if (Array.isArray(responseData)) {
    return responseData;
  }
  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }
  if (Array.isArray(responseData?.results)) {
    return responseData.results;
  }
  return [];
};

/**
 * Maps backend activity to UI model
 * @param {ActivityApiModel} activity - The backend activity object to map
 * @returns {TimelineActivity} The mapped timeline activity object
 */
const mapActivityToTimeline = (activity: ActivityApiModel): TimelineActivity => ({
  id: activity._id,
  title: activity.title,
  description: activity.message,
  timestamp: activity.createdAt,
  chargingPointSn: activity.chargingPointSn,
  status: activity.status,
});

/**
 * Activity Timeline Component
 * Renders a list of activities in a vertical timeline.
 * @param {ActivityTimelineProperties} [properties] - Component properties
 * @returns {React.ReactElement} Activity timeline component
 */
function ActivityTimeline(properties?: ActivityTimelineProperties): React.ReactElement {
  const { userCustomId: propertyUserCustomId } = properties || {};
  const { t } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const { selectedEndConsumer } = useAppSelector((state) => state.endConsumerType);

  const [loading, setLoading] = useState<boolean>(true);
  const [activities, setActivities] = useState<TimelineActivity[]>([]);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });

  const activeUserCustomId =
    propertyUserCustomId || selectedEndConsumer?.userCustomId || searchParameters.get('userCustomId') || '';

  /**
   * Function to remove filter from the list of selected filters
   */
  const handleClearFilters = (): void => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Handle date change
   * @param {string} name - Name of the date range field
   * @param {string} value - Value of the date range field
   */
  const handleDateChange = (name: string, value: string): void => {
    setDateRange((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  useEffect(() => {
    let isMounted = true;

    /**
     * Fetches driver activity logs from the notifications API
     * @returns {Promise<void>} Resolves when activities are fetched
     */
    const fetchActivities = async (): Promise<void> => {
      if (!activeUserCustomId) {
        setLoading(false);
        return;
      }

      // If only one date is selected, do not call API
      if (
        (dateRange.startDate && !dateRange.endDate) ||
        (!dateRange.startDate && dateRange.endDate)
      ) {
        return;
      }

      try {
        setLoading(true);

        const query: Record<string, any> = {};

        // Add dates only if BOTH exist
        if (dateRange.startDate && dateRange.endDate) {
          query.startDate = dateRange.startDate;
          query.endDate = dateRange.endDate;
        }

        const response = await endConsumerV2Api.fetchActivityLogs(activeUserCustomId, query);

        if (isMounted) {
          const rawData = extractActivityList(response?.data);
          const mappedActivities = (rawData as ActivityApiModel[]).map(mapActivityToTimeline);
          setActivities(mappedActivities);
        }
      } catch (error: any) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || 'Something went wrong!',
            type: 'error',
            code: error?.response?.data?.code || 0,
          }),
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchActivities();

    return () => {
      isMounted = false;
    };
  }, [activeUserCustomId, dateRange.startDate, dateRange.endDate, dispatch]);

  if (loading) {
    return (
      <div className="activity-timeline-card">
        <div className="loading-container">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="activity-timeline-card">
      <div className="activity-timeline-filter-wrap">
        <div className="activity-timeline-filter">
          <CustomDatePicker
            inputLabel={t('startDate')}
            name="startDate"
            value={dateRange.startDate}
            onChangeValue={handleDateChange}
          />
          <CustomDatePicker
            inputLabel={t('endDate')}
            name="endDate"
            value={dateRange.endDate}
            onChangeValue={handleDateChange}
          />
        </div>
        <div className="activity-clear-filter">
          <p> </p>
          <MyButton
            label=""
            buttonType="none"
            onClickFunc={handleClearFilters}
            svgIcon={<ClearFilter />}
            hoverLabel={t('clearFilter')}
          />
        </div>
      </div>
      <div className="timeline-container">
        {activities.length === 0 ? (
          <div className="no-activity">{t('noActivitylogs', 'No activity logs')}</div>
        ) : (
          <div className="timeline-list">
            {activities.map((activity, index) => {
              const formattedTime =
                ConvertUTCtoDateTime(activity.timestamp) ||
                ConvertUTCtoDate(activity.timestamp) ||
                activity.timestamp;

              return (
                <div key={activity.id || `${activity.timestamp}-${index}`} className="timeline-item">
                  <div className="timeline-marker">
                    <div className="dot success" />
                    {index < activities.length - 1 && <div className="line" />}
                  </div>

                  <div className="timeline-content">
                    <div className="title-row">
                      <div className="title">
                        {t(activity.title, { defaultValue: activity.title })}
                      </div>
                      <div className="timestamp">{formattedTime}</div>
                    </div>

                    {activity.description && (
                      <div className="description">
                        {t(activity.description, { defaultValue: activity.description })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ActivityTimeline;
